/**
 * The databases the API reference can be written for, and their columns.
 */
import { useQueries, useQuery } from '@tanstack/react-query'

import type { Application, DatabaseApplication, Field, Workspace } from '@/api/types'
import { applicationsQuery } from '@/features/application/queries'
import { workspacesQuery } from '@/features/workspace/queries'
import { fieldsQuery } from '../table/queries'

export interface DocumentedDatabase {
  workspace: Workspace
  database: DatabaseApplication
}

function databasesOf(applications: readonly Application[]): DatabaseApplication[] {
  return applications.filter(
    (application): application is DatabaseApplication => application.type === 'database',
  )
}

/**
 * Every database the reader can see, under the workspace it belongs to.
 *
 * One query per workspace rather than a dedicated endpoint, because that is
 * what the sidebar already asks for -- so opening this page from inside the
 * application costs nothing new.
 */
export function useDocumentedDatabases(): {
  databases: DocumentedDatabase[]
  isPending: boolean
} {
  const workspaces = useQuery(workspacesQuery())
  const held = workspaces.data ?? []

  const results = useQueries({
    queries: held.map((workspace) => applicationsQuery(workspace.id)),
  })

  const databases = held.flatMap((workspace, index) =>
    databasesOf(results[index]?.data ?? []).map((database) => ({
      workspace,
      database,
    })),
  )

  return {
    databases,
    isPending: workspaces.isPending || results.some((one) => one.isPending),
  }
}

export interface TableFields {
  tableId: number
  fields: Field[]
}

/**
 * The columns of every table in a database.
 *
 * A table whose columns cannot be listed is left out rather than shown empty.
 * That happens for real: somebody can be given a view of a table without being
 * given the table, and the fields endpoint refuses them -- so an empty column
 * list here would describe a table with no columns, which is a different and
 * untrue thing.
 */
export function useTableFields(database: DatabaseApplication | undefined): {
  tables: TableFields[]
  isPending: boolean
} {
  const held = database?.tables ?? []
  const results = useQueries({
    queries: held.map((table) => fieldsQuery(table.id)),
  })

  const tables = held.flatMap((table, index) => {
    const fields = results[index]?.data
    return fields === undefined ? [] : [{ tableId: table.id, fields }]
  })

  return { tables, isPending: results.some((one) => one.isPending) }
}
